import React from 'react';
import { observer } from 'mobx-react-lite';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';

import { Config } from '@ratehub/base-ui';
import { isProviderRate } from '@ratehub/mtg-common';

import RefinanceStepNewMortgage from './RefinanceStepNewMortgage';
import RefinanceStepMortgageRate from './RefinanceStepMortgageRate';
import RefinanceStepPenaltyCalculation from './RefinanceStepPenaltyCalculation';
import RefinanceStepSummary from './RefinanceStepSummary';
import MortgageCalculator from './MortgageCalculator';


function RefinanceCalculator({ ...otherProps }) {
    return (
        <MortgageCalculator
            title={(
                <FormattedMessage
                    id="mtg-calculator.Refinance.calculatorTitle"
                    defaultMessage="Ratehub.ca's mortgage refinance calculator"
                />
            )}
            onSaveDocument={saveFormData}
            isSaveEnabled={Config.ENABLE_SAVING_DATA_FOR_CALCULATORS}
            {...otherProps}
        >
            <RefinanceStepNewMortgage
                title={(
                    <FormattedMessage
                        id="mtg-calculator.Refinance.newMortgage"
                        defaultMessage="Calculate your new mortgage amount"
                    />
                )}
                ctaText={(
                    <FormattedMessage
                        id="mtg-calculator.Refinance.nextStepCTA"
                        defaultMessage="Next step"
                    />
                )}
                ctaDataName="RefinanceCalculator.NewMortgageStep-continue"
                stepNumber={1}
                isInitiallyExpanded
            />

            <RefinanceStepMortgageRate
                title={(
                    <FormattedMessage
                        id="mtg-calculator.Refinance.newRate"
                        defaultMessage="Choose your new mortgage rate"
                    />
                )}
                ctaText={(
                    <FormattedMessage
                        id="mtg-calculator.Refinance.continueToPenaltyStep"
                        defaultMessage="Continue to penalty calculation"
                    />
                )}
                ctaDataName="RefinanceCalculator.NewMortgageRate-continue"
                stepNumber={2}
            />

            <RefinanceStepPenaltyCalculation
                title={(
                    <FormattedMessage
                        id="mtg-calculator.Refinance.penaltyCalculation"
                        defaultMessage="Calculate the penalty to break your mortgage"
                    />
                )}
                ctaText={(
                    <FormattedMessage
                        id="mtg-calculator.Refinance.penaltyStepCTA"
                        defaultMessage="Continue"
                    />
                )}
                ctaDataName="RefinanceCalculator.MortgagePenaltyStep-continue"
                stepNumber={3}
            />

            <RefinanceStepSummary
                title={(
                    <FormattedMessage
                        id="mtg-calculator.Refinance.refinanceSummary"
                        defaultMessage="Your refinance at a glance:"
                    />
                )}
                ctaText={(
                    <FormattedMessage
                        id="mtg-calculator.Refinance.getRateCTA"
                        defaultMessage="Get this rate"
                    />
                )}
                ctaDataName="RefinanceCalculator.SummaryStep-continue"
                stepNumber={4}
            />
        </MortgageCalculator>
    );
}

function saveFormData(document, values) {
    if (!document) {
        return;
    }

    const existingMortgageTerm = parseInt(values?.originalTermLength) || undefined;
    const selectedRate = values?.selectedRate;
    const city = values?.city;

    document.journey?.update({
        calculators: {
            property: {
                homeValue: values?.propertyValue,
                intendedUse: values?.propertyType,
                address: {
                    city: city?.name,
                    province: city?.province.code,
                },
                existingMortgage: {
                    currentMortgageBalance: values?.mortgageBalance,
                    type: values?.currentRateType,
                    term: existingMortgageTerm,
                    // renewal date = start date + term
                    renewalDate: existingMortgageTerm && values?.startDate 
                        ? dayjs(values.startDate).add(existingMortgageTerm, 'year').toISOString() 
                        : undefined,
                    paymentFrequencyType: values?.paymentFrequency,
                    payment: values?.mortgagePayment,
                    interestRate: values?.currentRate,
                },
            },
            mortgage: {
                additionalFundsRequired: values?.requiresAdditionalEquity ? values?.additionalEquity : undefined,
                type: values?.type,
                term: values?.term,
                amortization: parseInt(values?.amortization) * 12, // document amortization is in months
                // only save interest rate when custom rate is selected (not best rate)
                ...(!isProviderRate(selectedRate) && { interestRate: selectedRate?.value }),
            },
            provider: {
                slug: values?.existingProvider,
            },
            refinance: {
                knownPenaltyFee: values?.knownPenaltyFee,
            },
        },
    });
}

export default observer(RefinanceCalculator);
